
import React, { useState, useEffect } from "react";
import { X, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import logger from "@/utils/logger";

export interface TutorialStep {
  target: string;
  title: string;
  content: string;
  position?: "top" | "bottom" | "left" | "right";
}

interface TutorialSpotlightProps {
  steps: TutorialStep[];
  isOpen: boolean;
  onClose: () => void;
  pageId: string;
  showProgressBar?: boolean;
  highlightColor?: string;
  onStepChange?: (target: string | null) => void;
}

function chooseBestPosition(
  preferred: "top" | "bottom" | "left" | "right",
  rect: DOMRect,
  tooltipWidth: number,
  tooltipHeight: number,
  offset: number = 16
): "top" | "bottom" | "left" | "right" {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const spaceAbove = rect.top;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceLeft = rect.left;
  const spaceRight = viewportWidth - rect.right;

  const fitsTop = spaceAbove >= tooltipHeight + offset;
  const fitsBottom = spaceBelow >= tooltipHeight + offset;
  const fitsLeft = spaceLeft >= tooltipWidth + offset;
  const fitsRight = spaceRight >= tooltipWidth + offset;

  if (preferred === "top" && fitsTop) return "top";
  if (preferred === "bottom" && fitsBottom) return "bottom";
  if (preferred === "left" && fitsLeft) return "left";
  if (preferred === "right" && fitsRight) return "right";

  if (fitsBottom) return "bottom";
  if (fitsTop) return "top";
  if (fitsRight) return "right";
  if (fitsLeft) return "left";

  return preferred;
}

const TutorialSpotlight: React.FC<TutorialSpotlightProps> = ({
  steps,
  isOpen,
  onClose,
  pageId,
  showProgressBar = true,
  highlightColor,
  onStepChange,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const { theme } = useThemeConfig();
  const [isReady, setIsReady] = useState(false);
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  const spotlightColor = theme.primaryColor || '#9b87f5';

  // ✅ FIX: Assicuriamo che currentStep sia sempre definito prima di tutti gli hook
  const currentStep = steps && steps.length > 0 && currentStepIndex < steps.length
    ? steps[currentStepIndex]
    : null;

  useEffect(() => {
    if (onStepChange) {
      if (isOpen && currentStep) {
        onStepChange(currentStep.target);
      } else {
        onStepChange(null);
      }
    }
  }, [currentStep, isOpen, onStepChange]);

  const waitForStablePosition = (
    target: Element,
    onStable: (rect: DOMRect) => void,
    maxAttempts = 10,
    interval = 50
  ) => {
    let lastTop = -1;
    let attempts = 0;

    const check = () => {
      const rect = target.getBoundingClientRect();
      const top = rect.top + window.scrollY;

      if (Math.abs(top - lastTop) < 1) {
        onStable(rect);
      } else if (attempts < maxAttempts) {
        lastTop = top;
        attempts++;
        setTimeout(check, interval);
      } else {
        onStable(rect);
      }
    };

    check();
  };

  // Evita di azzerare continuamente l'overlay per prevenire lampeggi
  // durante il ricalcolo delle posizioni
  useEffect(() => {
    if (!isOpen || !currentStep || steps.length === 0) {
      if (isReady) {
        setIsReady(false);
      }
      return;
    }
    const target = document.querySelector(currentStep.target);
    if (!target) {
      logger.warn(`Tutorial target not found: ${currentStep.target}`);
      if (currentStepIndex < steps.length - 1) {
        setCurrentStepIndex(prev => prev + 1);
      } else {
        onClose();
        setCurrentStepIndex(0);
      }
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
        let stableScrollY = window.scrollY;
        let stableScrollX = window.scrollX;

        const checkStableScroll = () => {
          if (window.scrollY !== stableScrollY || window.scrollX !== stableScrollX) {
            stableScrollY = window.scrollY;
            stableScrollX = window.scrollX;
            requestAnimationFrame(checkStableScroll);
          } else {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const targetRect = target.getBoundingClientRect();
                
                const hasScrolled = stableScrollY > 0;
                const scrollOffsetFactor = 0.75;
                const dynamicOffset = window.scrollY * scrollOffsetFactor;
                const applyOffset = targetRect.top > window.innerHeight * 0.5;
                const top = targetRect.top - (hasScrolled ? window.scrollY : 0 ) - (applyOffset ? dynamicOffset : 0);
                const left = targetRect.left + window.scrollX;

                setPosition({
                  top,
                  left,
                  width: targetRect.width,
                  height: targetRect.height
                });

                setIsReady(true);
              });
            });
          }
        };

        checkStableScroll();
      } else {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, { threshold: [0.1] });

    observer.observe(target);
    
    const updateSpotlightPosition = () => {
      if (!currentStep) return;
      const targetElement = document.querySelector(currentStep.target);

      if (!targetElement) {
        return;
      }

      waitForStablePosition(targetElement, (rect) => {
        setPosition({
          top: rect.top,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height,
        });
      });
    };

    window.addEventListener("resize", updateSpotlightPosition);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSpotlightPosition);
    };
  }, [isOpen, currentStepIndex, steps, currentStep, onClose, isReady]);

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      onClose();
      setCurrentStepIndex(0);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const handleClose = () => {
    onClose();
    setCurrentStepIndex(0);
  };

  const getTooltipPosition = () => {
    if (!currentStep) return {};

    const offset = 16;
    const tooltipWidth = 280;
    const tooltipHeight = 180;

    const rect = new DOMRect(
      position.left - window.scrollX,
      position.top - window.scrollY,
      position.width,
      position.height
    );

    const bestPosition = chooseBestPosition(
      currentStep.position || 'bottom',
      rect,
      tooltipWidth,
      tooltipHeight,
      offset
    );

    let left = position.left + position.width / 2;
    let top = position.top + position.height + offset;
    let transform = 'translateX(-50%)';

    switch (bestPosition) {
      case 'top':
        top = position.top - offset - tooltipHeight;
        transform = 'translateX(-50%)';
        break;
      case 'bottom':
        top = position.top + position.height + offset;
        transform = 'translateX(-50%)';
        break;
      case 'left':
        left = position.left - offset;
        top = position.top + position.height / 2;
        transform = 'translateX(-100%) translateY(-50%)';
        break;
      case 'right':
        left = position.left + position.width + offset;
        top = position.top + position.height / 2;
        transform = 'translateY(-50%)';
        break;
    }

    const viewportWidth = window.innerWidth;
    if (left - tooltipWidth / 2 < 20) {
      left = 20 + tooltipWidth / 2;
    } else if (left + tooltipWidth / 2 > viewportWidth - 20) {
      left = viewportWidth - 20 - tooltipWidth / 2;
    }

    return { left, top, transform };
  };
  const isVisible = isOpen && steps.length > 0 && currentStep && isReady;
  const tooltipPosition = getTooltipPosition();
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  return (
    <div
      className="fixed inset-0 z-[11000] bg-black/50 flex items-center justify-center transition-opacity"
      role="dialog"
      aria-modal="true"
      // Manteniamo il nodo montato e controlliamo visibilità/interaction via CSS
      style={{
        visibility: isVisible ? 'visible' : 'hidden',
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      <div
        className="fixed rounded-md"
        style={{
          top: position.top,
          left: position.left,
          width: position.width,
          height: position.height,
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.75), 0 0 0 4px ${theme.errorColor}`,
          zIndex: 11001
        }}
      />

      <div
        ref={tooltipRef}
        className="absolute p-4 rounded-md shadow-lg max-w-xs z-[11005] w-[280px]"
        style={{
          color: theme.sectionTitleColor || 'black',
          backgroundColor: theme.cardBackgroundColor || 'white',
          borderRadius: theme.borderRadius || '0.5rem',
          ...tooltipPosition,
        }}
      >
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-medium">{currentStep?.title}</h3>
          <Button
            size="icon"
            variant="ghost"
            onClick={handleClose}
            className="h-6 w-6"
            aria-label="Chiudi tutorial"
            style={{ borderRadius: theme.buttonBorderRadius || '0.375rem' }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <p className="text-sm mb-4" style={{ color: theme.sectionTextColor }}>
          {currentStep?.content.split('\n').map((line, idx) => (
            <span key={idx}>
              {line}
              <br />
            </span>
          ))}
        </p>

        {showProgressBar && (
          <div
            className="w-full h-1 rounded-full mb-3"
            style={{ backgroundColor: theme.borderColor }}
          >
            <div
              className="h-full rounded-full"
              style={{ width: `${progress}%`, backgroundColor: spotlightColor }}
            />
          </div>
        )}

        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
            disabled={currentStepIndex === 0}
            className="px-2"
            style={{ borderRadius: theme.buttonBorderRadius || '0.375rem',
              backgroundColor: theme.backgroundColor,
              color: theme.primaryColor || 'black',
             }}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Indietro
          </Button>

          <div className="text-xs text-center">
            {currentStepIndex + 1} / {steps.length}
          </div>

          <Button
            variant="default"
            size="sm"
            onClick={handleNext}
            className="px-2"
            style={{ backgroundColor: spotlightColor, 
              borderRadius: theme.buttonBorderRadius || '0.375rem',
              color: theme.backgroundColor || theme.textColor,
            }}
          >
            {currentStepIndex === steps.length - 1 ? "Fine" : "Avanti"}
            {currentStepIndex < steps.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TutorialSpotlight;
